import { useEffect, useRef } from "react";
import { supabase } from "../config/supabaseClient";
import { validateAndSyncSdfUser } from "../utils/authSync";
import { preloadStaticDataSources, clearStaticDataSourceCache } from "../constant/staticDataSources";

/**
 * Ensures localStorage sdfUser stays in sync with the Supabase auth session (JWT).
 * Runs on mount and whenever auth state changes (e.g. login in another tab).
 * Prevents mismatch where sdfUser shows one user but API requests use another,
 * which causes RLS to return empty data.
 */
const AuthSync = () => {
  const syncTimeoutRef = useRef(null);
  const lastSyncTimeRef = useRef(0);
  const DEBOUNCE_MS = 1000; // Prevent rapid successive sync calls

  useEffect(() => {
    let mounted = true;

    const runSync = async () => {
      if (!mounted) return;

      const sdfUserStr = localStorage.getItem("sdfUser");
      if (!sdfUserStr) {
        return;
      }
      const now = Date.now();
      const timeSinceLastSync = now - lastSyncTimeRef.current;
      
      if (timeSinceLastSync < DEBOUNCE_MS) {
        if (syncTimeoutRef.current) {
          clearTimeout(syncTimeoutRef.current);
        }
        const remainingTime = Math.max(0, DEBOUNCE_MS - timeSinceLastSync);
        syncTimeoutRef.current = setTimeout(() => {
          if (mounted) {
            lastSyncTimeRef.current = Date.now();
            validateAndSyncSdfUser();
          }
        }, remainingTime);
        return;
      }

      lastSyncTimeRef.current = now;
      await validateAndSyncSdfUser();
    };

    runSync();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event) => {
        if (!mounted) return;
        
        if (event === "SIGNED_OUT") {
          clearStaticDataSourceCache();
          return;
        }
        
        if (event === "SIGNED_IN") {
          clearStaticDataSourceCache();
          preloadStaticDataSources().catch(err => {
            console.error('Error preloading static data sources:', err);
          });
        }
        
        if (["SIGNED_IN", "TOKEN_REFRESHED", "USER_UPDATED"].includes(event)) {
          runSync();
        }
      }
    );

    return () => {
      mounted = false;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      subscription?.unsubscribe();
    };
  }, []);

  return null;
};

export default AuthSync;
